import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", phone: "", address: "" };

export default function Transporters() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => setItems((await api.get("/transporters")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (editId) await api.put(`/transporters/${editId}`, form);
    else await api.post("/transporters", form);
    toast.success("Saved");
    setOpen(false); load();
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-4xl mx-auto" data-testid="transporters-page">
      <PageHeader
        title="Transporters"
        subtitle="Vendors / consignors who own and run the trucks. Track per-transporter payables."
        action={
          <Button data-testid="add-transporter-btn"
            onClick={() => { setEditId(null); setForm(empty); setOpen(true); }}
            className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add Transporter
          </Button>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-3 py-3">Phone</th>
              <th className="text-left px-3 py-3">Address</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-brand-muted text-sm">No transporters yet</td></tr>}
            {items.map((t) => (
              <tr key={t.id} className="border-t border-brand-line hover:bg-secondary/40" data-testid={`transporter-row-${t.id}`}>
                <td className="px-5 py-3 font-medium">
                  <Link to={`/ledger/transporter/${t.id}`} className="hover:text-brand-forest hover:underline">{t.name}</Link>
                </td>
                <td className="px-3 py-3">{t.phone || "—"}</td>
                <td className="px-3 py-3 text-brand-muted">{t.address || "—"}</td>
                <td className="px-5 py-3 text-right">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditId(t.id); setForm({ ...empty, ...t }); setOpen(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-terracotta"
                    onClick={async () => { if (confirm("Delete?")) { await api.delete(`/transporters/${t.id}`); load(); } }}
                    data-admin-only="true">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Transporter</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input data-testid="transporter-name" className="mt-1.5 h-10 rounded-sm bg-white" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Phone</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Address</Label><Input className="mt-1.5 h-10 rounded-sm bg-white" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} data-testid="save-transporter-btn" className="rounded-sm bg-brand-forest text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
